import Check from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import type { StepIconProps } from '@mui/material/StepIcon';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { FC, ReactNode } from 'react';
import { NAVY, RADIUS_CARD, SHADOW, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

const SidebarStepConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.vertical}`]: {
    marginLeft: 17,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: YELLOW,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundColor: YELLOW,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    width: 3,
    minHeight: 28,
    border: 0,
    backgroundColor: SURFACE_CONTAINER_LOW,
    borderRadius: 2,
  },
}));

const getStepIconStyles = (ownerState: { active?: boolean; completed?: boolean }) => {
  if (ownerState.completed) {
    return { backgroundColor: YELLOW, color: NAVY };
  }
  if (ownerState.active) {
    return { backgroundColor: YELLOW, color: NAVY, boxShadow: '0 2px 8px rgba(249, 208, 10, 0.4)' };
  }
  return { backgroundColor: SURFACE_CONTAINER_LOW, color: NAVY, opacity: 0.35 };
};

const StepIconRoot = styled('div')<{ ownerState: { active?: boolean; completed?: boolean } }>(({ ownerState }) => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.85rem',
  fontWeight: 600,
  transition: 'all 0.3s ease',
  ...getStepIconStyles(ownerState),
}));

const CustomStepIcon: FC<StepIconProps> = (props) => {
  const { active, completed, icon } = props;
  return (
    <StepIconRoot ownerState={{ active, completed }}>{completed ? <Check sx={{ fontSize: 18 }} /> : icon}</StepIconRoot>
  );
};

interface StepperSidebarProps {
  /** Title displayed in the navy header. */
  title: string;
  /** Subtitle displayed below the title (optional). */
  subtitle?: string;
  /** Array of step labels. */
  steps: string[];
  /** Zero-based index of the current active step. */
  activeStep: number;
  /** Optional extra content rendered below the stepper. */
  children?: ReactNode;
}

const StepperSidebar: FC<StepperSidebarProps> = ({ title, subtitle, steps, activeStep, children }) => (
  <Box
    sx={{
      width: 260,
      minWidth: 260,
      bgcolor: SURFACE_CONTAINER_LOWEST,
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      borderRadius: RADIUS_CARD,
      m: 2,
      mr: 0,
      boxShadow: SHADOW,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{ bgcolor: NAVY, px: 2.5, py: 2.5 }}>
      <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '1.05rem', lineHeight: 1.3 }}>{title}</Typography>
      {subtitle && (
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', mt: 0.25 }}>{subtitle}</Typography>
      )}
    </Box>

    {/* Progress */}
    <Box sx={{ px: 2.5, pt: 2 }}>
      <Typography
        variant="caption"
        sx={{
          color: NAVY,
          opacity: 0.4,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.65rem',
        }}
      >
        Paso {activeStep + 1} de {steps.length}
      </Typography>
      <Box sx={{ bgcolor: SURFACE_CONTAINER_LOW, borderRadius: 2, height: 3, mt: 0.75, overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            width: `${((activeStep + 1) / steps.length) * 100}%`,
            backgroundColor: YELLOW,
            borderRadius: 2,
            transition: 'width 0.4s ease',
          }}
        />
      </Box>
    </Box>

    {/* Stepper */}
    <Box sx={{ px: 1.5, py: 2, flex: 1 }}>
      <Stepper
        activeStep={activeStep}
        orientation="vertical"
        connector={<SidebarStepConnector />}
        sx={{
          '& .MuiStepLabel-label': {
            fontSize: '0.82rem',
            fontWeight: 500,
            color: NAVY,
            opacity: 0.4,
            transition: 'all 0.2s ease',
          },
          '& .MuiStepLabel-label.Mui-active': {
            fontWeight: 700,
            opacity: 1,
            color: NAVY,
          },
          '& .MuiStepLabel-label.Mui-completed': {
            fontWeight: 600,
            opacity: 0.7,
            color: NAVY,
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel slots={{ stepIcon: CustomStepIcon }}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>

    {children}
  </Box>
);

export default StepperSidebar;
